import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Application, Container, Graphics } from 'pixi.js';
import { getStrokePoints } from 'perfect-freehand';

import { Camera, CanvasElement, Point } from '@/features/canvas/types';
import type { Draft } from './overlay-svg';
import { connectorGeometry, jumpedPolyPath, segIntersect } from './geometry';

/** Imperative handle so the controller can drive the GPU stage transform per animation frame without a
 *  React re-render (used by the imperative camera during pan/zoom). */
export interface PixiInkHandle {
  setCamera(cam: Camera): void;
  /** During an element drag the controller drives ONLY the connectors/ink touching the dragged ids
   *  imperatively (offset by dx,dy), instead of re-syncing the whole scene per frame via React. Pass
   *  `ids: null` to clear (the next full [elements] sync repaints everything at the committed spots). */
  setDragOffset(ids: Set<string> | null, dx: number, dy: number): void;
}

const FREEHAND_DEFAULT = '#1f2430';
const CONNECTOR_DEFAULT = '#868e96';
const SELECT = '#2ec5e8';
const INK_THINNING = 0.75; // matches the SVG/pen thinning
const PTRDEBUG = typeof window !== 'undefined' && window.location.search.includes('ptrdebug');

/** Half-width at a given pressure (perfect-freehand-like mapping). */
function radiusAt(size: number, pressure: number): number {
  return (size / 2) * Math.max(0.14, 1 - INK_THINNING * (1 - (pressure ?? 0.5)));
}

/** A smoothed centerline sample: world position + pressure. */
type CP = { x: number; y: number; p: number };

const FLATNESS_WORLD = 0.35; // max chord error vs the true curve, WORLD px (sub-pixel for zoom ≲ 3×)
// Soft cap on the CURVATURE-adaptive samples per stroke. Past it, a long stroke keeps drawing but its tail
// coarsens (one sample per centerline segment) instead of TRUNCATING — the old behaviour made a long
// stroke visibly stop partway.
const MAX_SAMPLES = 3000;
const MAX_DEPTH = 7; // bézier subdivision recursion cap
const JOIN_DOT = 0.7; // add a round join disc where a turn is sharper than this
const CR_ALPHA = 0.5; // CENTRIPETAL Catmull-Rom → no loops/cusps on sharp cursive turns
const HIGHLIGHTER_ALPHA = 0.38; // translucency of the marker/highlighter fill

const midCP = (a: CP, b: CP): CP => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, p: (a.p + b.p) / 2 });

/** Perpendicular distance of c from the chord a→b (flatness test). */
function distToLine(c: CP, a: CP, b: CP): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  return Math.abs((c.x - a.x) * dy - (c.y - a.y) * dx) / len;
}

/** Drop points closer than `eps` (Catmull-Rom needs non-zero knot intervals). */
function dedupeCP(pts: CP[], eps: number): CP[] {
  const out: CP[] = [pts[0]];
  for (let i = 1; i < pts.length; i++) {
    const q = out[out.length - 1];
    if (Math.hypot(pts[i].x - q.x, pts[i].y - q.y) > eps) out.push(pts[i]);
  }
  return out;
}

/** Closed-form centripetal Catmull-Rom span [p1,p2] → cubic bézier control points (near p1, near p2). */
function crToBezier(p0: CP, p1: CP, p2: CP, p3: CP, alpha: number): [CP, CP] {
  const d1 = Math.pow(Math.hypot(p1.x - p0.x, p1.y - p0.y), alpha) || 1e-5;
  const d2 = Math.pow(Math.hypot(p2.x - p1.x, p2.y - p1.y), alpha) || 1e-5;
  const d3 = Math.pow(Math.hypot(p3.x - p2.x, p3.y - p2.y), alpha) || 1e-5;
  const b1x = (p2.x * d1 * d1 - p0.x * d2 * d2 + p1.x * (2 * d1 * d1 + 3 * d1 * d2 + d2 * d2)) / (3 * d1 * (d1 + d2));
  const b1y = (p2.y * d1 * d1 - p0.y * d2 * d2 + p1.y * (2 * d1 * d1 + 3 * d1 * d2 + d2 * d2)) / (3 * d1 * (d1 + d2));
  const b2x = (p1.x * d3 * d3 - p3.x * d2 * d2 + p2.x * (2 * d3 * d3 + 3 * d3 * d2 + d2 * d2)) / (3 * d3 * (d3 + d2));
  const b2y = (p1.y * d3 * d3 - p3.y * d2 * d2 + p2.y * (2 * d3 * d3 + 3 * d3 * d2 + d2 * d2)) / (3 * d3 * (d3 + d2));
  return [
    { x: b1x, y: b1y, p: p1.p },
    { x: b2x, y: b2y, p: p2.p },
  ];
}

/** Adaptive de Casteljau flatten of the cubic (a,c1,c2,b); pushes interior points + endpoint b. */
function flattenBezier(a: CP, c1: CP, c2: CP, b: CP, tol: number, depth: number, out: CP[], budget: { n: number }) {
  if (depth <= 0 || budget.n >= MAX_SAMPLES || distToLine(c1, a, b) + distToLine(c2, a, b) <= tol) {
    out.push({ x: b.x, y: b.y, p: b.p });
    budget.n++;
    return;
  }
  const ab = midCP(a, c1);
  const bc = midCP(c1, c2);
  const cd = midCP(c2, b);
  const abc = midCP(ab, bc);
  const bcd = midCP(bc, cd);
  const m = midCP(abc, bcd);
  m.p = (a.p + b.p) / 2;
  flattenBezier(a, ab, abc, m, tol, depth - 1, out, budget);
  flattenBezier(m, bcd, cd, b, tol, depth - 1, out, budget);
}

/** Smooth + curve-fit a raw pressure stroke into a dense, even centerline (streamline low-pass →
 *  centripetal Catmull-Rom → curvature-adaptive flatten). Shared by the pen ribbon AND the highlighter so
 *  both read the exact same curve. Returns [] or a single point for degenerate input. */
function smoothCenterline(world: number[][], size: number): CP[] {
  let cl: CP[];
  try {
    const sp = getStrokePoints(world, { size, thinning: INK_THINNING, smoothing: 0.6, streamline: 0.5, simulatePressure: false, last: true });
    cl = sp.map((s) => ({ x: s.point[0], y: s.point[1], p: s.pressure ?? 0.5 }));
  } catch {
    cl = world.map((w) => ({ x: w[0], y: w[1], p: w[2] ?? 0.5 }));
  }
  if (cl.length < 2) cl = world.map((w) => ({ x: w[0], y: w[1], p: w[2] ?? 0.5 }));
  cl = dedupeCP(cl, 0.05);
  if (cl.length < 2) return cl;
  const flat: CP[] = [{ x: cl[0].x, y: cl[0].y, p: cl[0].p }];
  const budget = { n: 1 };
  for (let i = 0; i < cl.length - 1; i++) {
    const p0 = cl[i - 1] ?? cl[i];
    const p1 = cl[i];
    const p2 = cl[i + 1];
    const p3 = cl[i + 2] ?? cl[i + 1];
    const [c1, c2] = crToBezier(p0, p1, p2, p3, CR_ALPHA);
    // Once the budget is spent, flattenBezier's base case emits just the segment endpoint (no subdivision),
    // so the REST of the stroke still draws — coarser, but never cut off. (No `break`: that truncated it.)
    flattenBezier(p1, c1, c2, p2, FLATNESS_WORLD, MAX_DEPTH, flat, budget);
  }
  return flat;
}

/**
 * Build a premium variable-width ink ribbon from the stroke centerline. The centerline is first
 * STREAMLINE-smoothed (perfect-freehand getStrokePoints — the smoothing step the SVG path always ran but
 * the GPU path skipped), then curve-fit with a CENTRIPETAL Catmull-Rom spline and adaptively resampled in
 * WORLD space — so a stroke drawn zoomed-way-out is still a smooth curve, not a faceted polygon (linear
 * densify could never add curvature; that was the "ugly/angular, worse zoomed-out" bug). The ribbon is
 * drawn as many convex, OPAQUE, same-colour pieces (per-segment trapezoids + round caps + sharp-turn join
 * discs): overlaps just repaint, so self-intersecting/cursive strokes never earcut into a solid "blob".
 * Ends taper to a soft point for an Apple-Pencil feel. Vertices are bounded (MAX_SAMPLES) so perf holds.
 */
function buildInk(g: Graphics, world: number[][], size: number, color: string, highlighter = false) {
  if (!world || world.length === 0) {
    clearHlMask(g);
    return;
  }
  if (highlighter) {
    buildHighlighter(g, world, size, color);
    return;
  }
  clearHlMask(g); // pen path: make sure this Graphics isn't still clipped by a leftover highlighter mask
  if (world.length === 1) {
    g.circle(world[0][0], world[0][1], radiusAt(size, world[0][2] ?? 0.5)).fill({ color });
    return;
  }
  const flat = smoothCenterline(world, size);
  const n = flat.length;
  if (n === 0) return;
  if (n === 1) {
    g.circle(flat[0].x, flat[0].y, radiusAt(size, flat[0].p)).fill({ color });
    return;
  }
  // 4) running length + per-sample half-width, tapering to a soft point at both ends
  const dist = new Float32Array(n);
  for (let i = 1; i < n; i++) dist[i] = dist[i - 1] + Math.hypot(flat[i].x - flat[i - 1].x, flat[i].y - flat[i - 1].y);
  const total = dist[n - 1] || 1;
  const taper = Math.min(total * 0.5, size * 1.5);
  const ease = (d: number) => {
    const t = Math.min(1, d / (taper || 1));
    return t * (2 - t); // easeOutQuad
  };
  const half = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const base = radiusAt(size, flat[i].p);
    half[i] = taper > 0 ? base * Math.min(ease(dist[i]), ease(total - dist[i])) : base;
  }
  // 5) left/right rails from a centered finite-difference tangent (smooth centerline → stable normals)
  const Lx = new Float32Array(n);
  const Ly = new Float32Array(n);
  const Rx = new Float32Array(n);
  const Ry = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const a = flat[Math.max(0, i - 1)];
    const b = flat[Math.min(n - 1, i + 1)];
    let tx = b.x - a.x;
    let ty = b.y - a.y;
    const tl = Math.hypot(tx, ty) || 1;
    tx /= tl;
    ty /= tl;
    const nx = -ty;
    const ny = tx;
    const r = half[i];
    Lx[i] = flat[i].x + nx * r;
    Ly[i] = flat[i].y + ny * r;
    Rx[i] = flat[i].x - nx * r;
    Ry[i] = flat[i].y - ny * r;
  }
  // 6) ribbon trapezoids (2 triangles / segment) — opaque same-colour pieces, so overlaps just repaint
  for (let i = 0; i < n - 1; i++) {
    g.poly([Lx[i], Ly[i], Rx[i], Ry[i], Rx[i + 1], Ry[i + 1]]);
    g.poly([Lx[i], Ly[i], Rx[i + 1], Ry[i + 1], Lx[i + 1], Ly[i + 1]]);
  }
  // 7) round caps at both ends (a clean point when tapered)
  g.circle(flat[0].x, flat[0].y, Math.max(0.4, half[0]));
  g.circle(flat[n - 1].x, flat[n - 1].y, Math.max(0.4, half[n - 1]));
  // 8) round join discs only where the turn is genuinely sharp (rare after smoothing → bounded)
  for (let i = 1; i < n - 1; i++) {
    const ax = flat[i].x - flat[i - 1].x;
    const ay = flat[i].y - flat[i - 1].y;
    const bx = flat[i + 1].x - flat[i].x;
    const by = flat[i + 1].y - flat[i].y;
    const dot = (ax * bx + ay * by) / ((Math.hypot(ax, ay) || 1) * (Math.hypot(bx, by) || 1));
    if (dot < JOIN_DOT) g.circle(flat[i].x, flat[i].y, half[i]);
  }
  g.fill({ color }); // each sub-shape triangulated independently — never one self-intersecting outline
}

const HL_MASK = '__hlmask';

/** Find (or create) the child Graphics used as a highlighter's coverage mask. */
function ensureHlMask(g: Graphics): Graphics {
  const found = g.children.find((c) => (c as unknown as Record<string, unknown>)[HL_MASK]) as Graphics | undefined;
  if (found) return found;
  const m = new Graphics();
  (m as unknown as Record<string, unknown>)[HL_MASK] = true;
  g.addChild(m);
  return m;
}

/** Detach + empty any highlighter mask on `g` (so a pen / cleared draft on the same Graphics isn't clipped,
 *  and the now-empty mask child renders nothing). */
function clearHlMask(g: Graphics): void {
  if (g.mask) g.mask = null;
  const m = g.children.find((c) => (c as unknown as Record<string, unknown>)[HL_MASK]) as Graphics | undefined;
  if (m) m.clear();
}

/** Opaque COVERAGE ribbon for the highlighter mask: tiled quads + square (projecting) caps from the smooth
 *  centerline. Overlaps are irrelevant here — this only feeds the stencil, which is binary coverage. */
function coverageRibbon(g: Graphics, world: number[][], size: number): void {
  const half = size / 2;
  const c = smoothCenterline(world, size);
  const n = c.length;
  if (n === 0) return;
  if (n === 1) {
    g.rect(c[0].x - half, c[0].y - half, size, size);
    return;
  }
  // square caps: push the two endpoints out by half a width along the end tangents
  const ext = (from: CP, to: CP) => {
    const dx = from.x - to.x;
    const dy = from.y - to.y;
    const d = Math.hypot(dx, dy) || 1;
    return { x: from.x + (dx / d) * half, y: from.y + (dy / d) * half, p: from.p };
  };
  const P = c.slice();
  P[0] = ext(c[0], c[1]);
  P[n - 1] = ext(c[n - 1], c[n - 2]);
  const L: number[] = [];
  const R: number[] = [];
  for (let i = 0; i < n; i++) {
    const a = P[Math.max(0, i - 1)];
    const b = P[Math.min(n - 1, i + 1)];
    let tx = b.x - a.x;
    let ty = b.y - a.y;
    const tl = Math.hypot(tx, ty) || 1;
    tx /= tl;
    ty /= tl;
    const nx = -ty;
    const ny = tx;
    L.push(P[i].x + nx * half, P[i].y + ny * half);
    R.push(P[i].x - nx * half, P[i].y - ny * half);
  }
  for (let i = 0; i < n - 1; i++) {
    g.poly([L[2 * i], L[2 * i + 1], R[2 * i], R[2 * i + 1], R[2 * i + 2], R[2 * i + 3]]);
    g.poly([L[2 * i], L[2 * i + 1], R[2 * i + 2], R[2 * i + 3], L[2 * i + 2], L[2 * i + 3]]);
  }
}

/**
 * Highlighter/marker: a translucent, uniform-width, SQUARE-capped stroke. A translucent stroke can't be
 * drawn as overlapping geometry — every self-overlap (joins, caps, curve folds, or a self-intersecting
 * outline) double-blends and shows as beading/banding. So we decouple COVERAGE from FILL, the way
 * Procreate/GoodNotes do: render the stroke shape into a child Graphics used as a MASK (the stencil is
 * binary, so overlaps in the coverage ribbon are harmless), then fill the whole bounding box ONCE at
 * HIGHLIGHTER_ALPHA through that mask. Result: perfectly even alpha regardless of shape, no beading, no
 * holes, and — unlike a cached texture — fully resolution-independent (crisp at any zoom).
 */
function buildHighlighter(g: Graphics, world: number[][], size: number, color: string) {
  if (world.length < 2) {
    clearHlMask(g);
    if (world.length === 1) g.rect(world[0][0] - size / 2, world[0][1] - size / 2, size, size).fill({ color, alpha: HIGHLIGHTER_ALPHA });
    return;
  }
  const mask = ensureHlMask(g);
  mask.clear();
  coverageRibbon(mask, world, size);
  mask.fill({ color: 0xffffff });
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const w of world) {
    if (w[0] < minX) minX = w[0];
    if (w[1] < minY) minY = w[1];
    if (w[0] > maxX) maxX = w[0];
    if (w[1] > maxY) maxY = w[1];
  }
  g.rect(minX - size, minY - size, maxX - minX + 2 * size, maxY - minY + 2 * size).fill({ color, alpha: HIGHLIGHTER_ALPHA });
  g.mask = mask;
}

/** Sample any SVG path `d` into world points via a hidden path element (handles beziers + arc line-jumps
 *  uniformly, so a connector's exact SVG geometry is reproduced on the GPU). */
function makePathSampler(host: HTMLElement) {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('style', 'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none');
  const path = document.createElementNS(ns, 'path');
  svg.appendChild(path);
  host.appendChild(svg);
  return {
    sample(d: string, step: number): Point[] {
      path.setAttribute('d', d);
      let len = 0;
      try {
        len = path.getTotalLength();
      } catch {
        return [];
      }
      if (!len) return [];
      const out: Point[] = [];
      for (let l = 0; l <= len; l += step) {
        const p = path.getPointAtLength(l);
        out.push({ x: p.x, y: p.y });
      }
      const e = path.getPointAtLength(len);
      out.push({ x: e.x, y: e.y });
      return out;
    },
    destroy() {
      svg.remove();
    },
  };
}

/** Filled arrowhead triangle at `tip`, pointing along the incoming direction from `from`. */
function drawArrow(g: Graphics, tip: Point, from: Point, color: string) {
  const ang = Math.atan2(tip.y - from.y, tip.x - from.x);
  const len = 12;
  const spread = 0.4;
  g.poly([
    tip.x,
    tip.y,
    tip.x - len * Math.cos(ang - spread),
    tip.y - len * Math.sin(ang - spread),
    tip.x - len * Math.cos(ang + spread),
    tip.y - len * Math.sin(ang + spread),
  ]).fill({ color });
}

/** Render a connector's sampled polyline as solid / dashed / dotted onto a Graphics. */
function drawConnectorLine(g: Graphics, pts: Point[], color: string, width: number, lineStyle: string | undefined) {
  if (pts.length < 2) return;
  const dash = lineStyle === 'dashed' ? [width * 4, width * 3] : lineStyle === 'dotted' ? [width, width * 2.5] : null;
  const stroke = { color, width, cap: 'round' as const, join: 'round' as const };
  if (!dash) {
    g.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
    g.stroke(stroke);
    return;
  }
  // Walk the polyline, toggling on/off dash runs.
  let on = true;
  let rem = dash[0];
  let cur = pts[0];
  let penDown = false;
  for (let i = 1; i < pts.length; i++) {
    let a = cur;
    const b = pts[i];
    let segLen = Math.hypot(b.x - a.x, b.y - a.y);
    const ux = segLen ? (b.x - a.x) / segLen : 0;
    const uy = segLen ? (b.y - a.y) / segLen : 0;
    while (segLen > 0) {
      const take = Math.min(rem, segLen);
      const nx = a.x + ux * take;
      const ny = a.y + uy * take;
      if (on) {
        if (!penDown) {
          g.moveTo(a.x, a.y);
          penDown = true;
        }
        g.lineTo(nx, ny);
      } else {
        penDown = false;
      }
      a = { x: nx, y: ny };
      segLen -= take;
      rem -= take;
      if (rem <= 0.0001) {
        on = !on;
        rem = dash[on ? 0 : 1];
        if (!on) penDown = false;
      }
    }
    cur = b;
  }
  g.stroke(stroke);
}

/**
 * GPU (WebGL) layer for committed freehand ink + connectors. Each ink stroke's outline is tessellated
 * ONCE (when its geometry/colour/selection changes) and uploaded to the GPU; panning/zooming only updates
 * the world container's transform, so the GPU re-transforms everything for free — no CPU re-rasterisation
 * (the SVG bottleneck). The canvas is pointer-transparent; ink/connector hit-testing is done in the
 * controller (with a transparent SVG hit-layer for connectors).
 */
type Props = { elements: CanvasElement[]; cam: Camera; selectedIds: Set<string>; draft: Draft; byId: (id: string) => CanvasElement | undefined };

const PixiInkLayer = forwardRef<PixiInkHandle, Props>(function PixiInkLayer({ elements, cam, selectedIds, draft, byId }, ref) {
  const hostRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application | null>(null);
  const worldRef = useRef<Container | null>(null);
  const draftLayerRef = useRef<Container | null>(null);
  const draftGfxRef = useRef<Graphics | null>(null);
  const gfxRef = useRef<Map<string, Graphics>>(new Map());
  const sigRef = useRef<Map<string, string>>(new Map());
  const samplerRef = useRef<ReturnType<typeof makePathSampler> | null>(null);
  const byIdRef = useRef(byId);
  byIdRef.current = byId;
  // Latest props for the async init to draw once it's ready.
  const propsRef = useRef({ elements, cam, selectedIds, draft });
  propsRef.current = { elements, cam, selectedIds, draft };
  // Non-null while an element drag is in flight — makes syncElements early-out (the drag is driven
  // imperatively via redrawDrag instead of a full per-frame rebuild).
  const dragRef = useRef<{ ids: Set<string>; dx: number; dy: number } | null>(null);

  // Drive BOTH the committed-ink container and the (separate render-group) draft container from the camera.
  const syncCamera = (c: Camera) => {
    const w = worldRef.current;
    const d = draftLayerRef.current;
    if (w) {
      w.position.set(c.x, c.y);
      w.scale.set(c.zoom);
    }
    if (d) {
      d.position.set(c.x, c.y);
      d.scale.set(c.zoom);
    }
  };

  // Render the in-progress pen stroke with the SAME ribbon builder as committed ink (so the live
  // preview is pixel-consistent and GPU-rendered). Non-pen drafts stay on the SVG DraftLayer.
  const syncDraft = (d: Draft) => {
    const g = draftGfxRef.current;
    if (!g) return;
    g.clear();
    if (d && d.kind === 'pen' && d.stroke.length) buildInk(g, d.stroke, d.width, d.color, d.highlighter);
    else clearHlMask(g); // no pen draft → drop any highlighter mask so the empty draft shows nothing
  };

  // While dragging: redraw ONLY the connectors/freehand touching the dragged ids, offsetting the moving
  // endpoint box by the live dx,dy. `els` holds PRE-move boxes (no setElements during the drag), so byId
  // returns originals and the offset yields the correct live geometry (both ends moving → rigid shift;
  // one end → recomputed; neither → skipped). Line-jumps are dropped mid-drag (restored by the drop sync).
  const redrawDrag = (world: Container, els: CanvasElement[], selected: Set<string>, ids: Set<string>, dx: number, dy: number) => {
    const bid = byIdRef.current;
    const sampler = samplerRef.current;
    const gfx = gfxRef.current;
    if (!sampler) return;
    const offsetBox = (el: CanvasElement) => (ids.has(el.id) ? { ...el, x: el.x + dx, y: el.y + dy } : el);
    for (const el of els) {
      if (el.kind === 'freehand') {
        if (ids.has(el.id)) gfx.get(el.id)?.position.set(dx, dy); // group-move ink: pure transform, no re-tessellate
        continue;
      }
      if (el.kind !== 'connector') continue;
      if (!((el.from_id && ids.has(el.from_id)) || (el.to_id && ids.has(el.to_id)))) continue;
      const f0 = el.from_id ? bid(el.from_id) : undefined;
      const t0 = el.to_id ? bid(el.to_id) : undefined;
      if (!f0 || !t0) continue;
      const f = offsetBox(f0);
      const t = offsetBox(t0);
      let g = gfx.get(el.id);
      if (!g) {
        g = new Graphics();
        world.addChild(g);
        gfx.set(el.id, g);
      }
      g.clear();
      g.zIndex = el.z_index;
      const geom = connectorGeometry(f, t, el.style ?? {});
      const sel = selected.has(el.id);
      const color = sel ? SELECT : el.color ?? CONNECTOR_DEFAULT;
      const width = (el.style?.strokeWidth ?? 2) + (sel ? 1 : 0);
      const d = geom.polyline ? jumpedPolyPath(geom.polyline, [], 6) : geom.d; // no line-jumps mid-drag
      drawConnectorLine(g, sampler.sample(d, 3), color, width, el.style?.lineStyle);
      if (el.style?.arrowEnd !== false) drawArrow(g, geom.end, geom.endFrom, color);
      if (el.style?.arrowStart) drawArrow(g, geom.start, geom.startFrom, color);
    }
  };

  const syncElements = (world: Container, els: CanvasElement[], selected: Set<string>) => {
    if (dragRef.current) return; // drag in flight → connectors/ink are driven imperatively (redrawDrag)
    const gfx = gfxRef.current;
    const sig = sigRef.current;
    const seen = new Set<string>();
    for (const el of els) {
      if (el.kind !== 'freehand') continue;
      seen.add(el.id);
      const sel = selected.has(el.id);
      const existing = gfx.get(el.id);
      // Clear any leftover imperative drag offset FIRST — even for a stroke we then skip (a group-move that
      // was aborted mid-drag doesn't change the signature, so without this the ink stays displaced).
      if (existing) existing.position.set(0, 0);
      const s = `${el.updated_at}|${el.color ?? ''}|${el.z_index}|${sel ? 1 : 0}|${el.style?.highlighter ? 'h' : ''}`;
      // Skip ONLY if unchanged AND its Graphics is still present — never trust the signature alone, so a
      // stroke that (ever) failed to build/paint is retried on the next sync instead of cached out forever.
      if (sig.get(el.id) === s && existing?.parent) continue;
      let g = existing;
      const isNew = !g;
      if (!g) {
        g = new Graphics();
        world.addChild(g);
        gfx.set(el.id, g);
      }
      const stroke = el.style?.stroke;
      const color = sel ? SELECT : el.color ?? FREEHAND_DEFAULT;
      const size = el.style?.strokeWidth ?? 8;
      try {
        g.clear();
        g.zIndex = el.z_index;
        const hl = el.style?.highlighter === true;
        if (stroke && stroke.length) {
          buildInk(g, stroke.map((p) => [el.x + p[0], el.y + p[1], p[2] ?? 0.5]), size, color, hl);
        } else if (el.style?.points && el.style.points.length >= 4) {
          const pts = el.style.points;
          const cl: number[][] = [];
          for (let i = 0; i < pts.length; i += 2) cl.push([el.x + pts[i], el.y + pts[i + 1], 0.5]);
          buildInk(g, cl, size, color, hl);
        }
        if (PTRDEBUG && isNew) {
          const gg = g;
          const app = appRef.current;
          requestAnimationFrame(() => {
            window.dispatchEvent(new CustomEvent('ptrlog', { detail: `ink ${el.id.slice(0, 4)} built=${gg.width > 0} kids=${world.children.length} fps=${app ? Math.round(app.ticker.FPS) : '?'}` }));
          });
        }
        sig.set(el.id, s); // record the signature ONLY after a successful build
      } catch {
        g.destroy({ children: true });
        gfx.delete(el.id);
        sig.delete(el.id); // never poison the cache — rebuild next sync
      }
    }

    // ---- connectors (few per board; always rebuilt because line-jumps depend on other connectors) ----
    const bid = byIdRef.current;
    const sampler = samplerRef.current;
    const conns = els.filter((e) => e.kind === 'connector');
    const polys = conns
      .map((e) => {
        const f = e.from_id ? bid(e.from_id) : undefined;
        const t = e.to_id ? bid(e.to_id) : undefined;
        return f && t ? { id: e.id, poly: connectorGeometry(f, t, e.style ?? {}).polyline } : null;
      })
      .filter((x): x is { id: string; poly: Point[] | null } => !!x);
    const jumpsFor = (id: string, poly: Point[] | null) => {
      const out: { i: number; point: Point }[] = [];
      if (!poly) return out;
      for (const o of polys) {
        if (o.id === id || !o.poly || id <= o.id) continue; // only one of each pair hops (deterministic by id)
        for (let i = 0; i < poly.length - 1; i++)
          for (let k = 0; k < o.poly.length - 1; k++) {
            const p = segIntersect(poly[i], poly[i + 1], o.poly[k], o.poly[k + 1]);
            if (p) out.push({ i, point: p });
          }
      }
      return out;
    };
    for (const el of conns) {
      seen.add(el.id);
      sig.delete(el.id); // not signature-cached
      let g = gfx.get(el.id);
      if (!g) {
        g = new Graphics();
        world.addChild(g);
        gfx.set(el.id, g);
      }
      g.clear();
      g.zIndex = el.z_index;
      const f = el.from_id ? bid(el.from_id) : undefined;
      const t = el.to_id ? bid(el.to_id) : undefined;
      if (!f || !t || !sampler) continue;
      const geom = connectorGeometry(f, t, el.style ?? {});
      const sel = selected.has(el.id);
      const color = sel ? SELECT : el.color ?? CONNECTOR_DEFAULT;
      const width = (el.style?.strokeWidth ?? 2) + (sel ? 1 : 0);
      const d = geom.polyline ? jumpedPolyPath(geom.polyline, jumpsFor(el.id, geom.polyline), 6) : geom.d;
      drawConnectorLine(g, sampler.sample(d, 3), color, width, el.style?.lineStyle);
      if (el.style?.arrowEnd !== false) drawArrow(g, geom.end, geom.endFrom, color);
      if (el.style?.arrowStart) drawArrow(g, geom.start, geom.startFrom, color);
    }

    for (const [id, g] of gfx) {
      if (!seen.has(id)) {
        g.destroy({ children: true });
        gfx.delete(id);
        sig.delete(id);
      }
    }
  };

  // Create the Pixi app once; draw the current state as soon as it's ready.
  useEffect(() => {
    let disposed = false;
    const app = new Application();
    app
      .init({ resizeTo: window, backgroundAlpha: 0, antialias: true, resolution: Math.min(window.devicePixelRatio || 1, 2), autoDensity: true, preference: 'webgl' })
      .then(() => {
        if (disposed) {
          app.destroy(true);
          return;
        }
        appRef.current = app;
        const world = new Container();
        world.sortableChildren = true;
        app.stage.addChild(world);
        worldRef.current = world;
        // The live draft is its OWN render group (a sibling of `world`, drawn above it). Its per-frame
        // clear()+rebuild then only invalidates a 1-child instruction set — it no longer forces pixi to
        // rebuild the entire committed-ink render group every frame (which starved new strokes at scale).
        const draftLayer = new Container();
        draftLayer.enableRenderGroup();
        app.stage.addChild(draftLayer);
        draftLayerRef.current = draftLayer;
        const draftGfx = new Graphics();
        draftLayer.addChild(draftGfx);
        draftGfxRef.current = draftGfx;
        if (hostRef.current) samplerRef.current = makePathSampler(hostRef.current);
        app.canvas.style.display = 'block';
        hostRef.current?.appendChild(app.canvas);
        const p = propsRef.current;
        syncCamera(p.cam);
        syncElements(world, p.elements, p.selectedIds);
        syncDraft(p.draft);
      });
    return () => {
      disposed = true;
      gfxRef.current.clear();
      sigRef.current.clear();
      worldRef.current = null;
      draftLayerRef.current = null;
      draftGfxRef.current = null;
      samplerRef.current?.destroy();
      samplerRef.current = null;
      if (appRef.current) {
        appRef.current.destroy(true);
        appRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Camera → GPU stage transform (cheap: no re-tessellation).
  useEffect(() => {
    syncCamera(cam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cam]);

  // Elements/selection → rebuild only the strokes whose signature changed.
  useEffect(() => {
    if (worldRef.current) syncElements(worldRef.current, elements, selectedIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements, selectedIds]);

  // In-progress pen stroke → redraw the draft ribbon.
  useEffect(() => {
    syncDraft(draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  // Imperative per-frame camera (no React re-render) for the imperative-camera path.
  useImperativeHandle(ref, () => ({
    setCamera(c: Camera) {
      syncCamera(c);
    },
    setDragOffset(ids: Set<string> | null, dx: number, dy: number) {
      const world = worldRef.current;
      if (!world) return;
      if (!ids) {
        dragRef.current = null; // clear only — the commit's setElements triggers the authoritative full sync
        return;
      }
      dragRef.current = { ids, dx, dy };
      const p = propsRef.current;
      redrawDrag(world, p.elements, p.selectedIds, ids, dx, dy);
    },
  }), []);

  // z-10 keeps the ink ABOVE the DOM elements (worldDom, z-auto) but BELOW the selection chrome (z-20)
  // and the floating UI (z-30). The EXPLICIT z-index matters on Safari: a z-auto WebGL canvas there can
  // composite on top of z-indexed HTML overlays (hiding the top bar), which an explicit layer order fixes.
  return <div ref={hostRef} className="pointer-events-none absolute inset-0 z-10" aria-hidden />;
});

export default PixiInkLayer;
